import {
  getPageHeader,
  Card,
  CardContent,
  Button,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderActionSecondary,
  PageHeaderActions,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  VisibleFilterFields,
} from '@yeshuman/sdk'
import { useEffect, useRef, useState } from 'react'
import { HealthListPageSkeleton } from './health-list-page-skeleton'
import { Thermometer, Plus, Pencil, Trash2 } from 'lucide-react'
import {
  useMeasurements,
  useCreateMeasurement,
  useUpdateMeasurement,
  useDeleteMeasurement,
  type MeasurementFilters,
  type MeasurementItem,
  type MeasurementTypeFilter,
  type CreateMeasurementData,
  type UpdateMeasurementData,
} from '../hooks/use-measurements'
import { toast } from 'sonner'
import { MeasurementStats } from './measurement-stats'
import { MeasurementFormDialog } from './measurement-form-dialog'
import { getHealthMeasurementFilterDefinitions } from '../list-filters/measurements'

const measurementFilterDefinitions = getHealthMeasurementFilterDefinitions()

function formatMeasuredAt(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}

function measurementSummary(m: MeasurementItem): string {
  if (m.measurement_type === 'body_weight' && m.mass_kg) return `${m.mass_kg} kg`
  if (m.measurement_type === 'blood_pressure')
    return `${m.systolic_mmhg}/${m.diastolic_mmhg} mmHg${m.pulse_bpm != null ? ` · pulse ${m.pulse_bpm}` : ''}`
  if (m.measurement_type === 'heart_rate' && m.bpm != null) return `${m.bpm} bpm`
  return ''
}

function typeLabel(t: string) {
  if (t === 'body_weight') return 'Weight'
  if (t === 'blood_pressure') return 'Blood pressure'
  if (t === 'heart_rate') return 'Heart rate'
  return t
}

export function MeasurementsPage() {
  const [filters, setFilters] = useState<MeasurementFilters>({ limit: 100 })
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editing, setEditing] = useState<MeasurementItem | null>(null)
  const [createDefaultsType, setCreateDefaultsType] = useState<MeasurementTypeFilter>('body_weight')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set())
  const previousRef = useRef<MeasurementItem[]>([])

  const { measurements, isLoading, error, refetch } = useMeasurements(filters)
  const createM = useCreateMeasurement()
  const updateM = useUpdateMeasurement()
  const deleteM = useDeleteMeasurement()

  useEffect(() => {
    if (measurements && previousRef.current.length > 0) {
      const changed = new Set<string>()
      measurements.forEach((m) => {
        const prev = previousRef.current.find((p) => p.id === m.id)
        if (!prev || JSON.stringify(prev) !== JSON.stringify(m)) changed.add(m.id)
      })
      if (changed.size > 0) {
        setHighlighted(changed)
        window.setTimeout(() => setHighlighted(new Set()), 3000)
      }
    }
    if (measurements) previousRef.current = measurements
  }, [measurements])

  const handleAdd = (t: MeasurementTypeFilter = 'body_weight') => {
    setEditing(null)
    setCreateDefaultsType(t)
    setIsFormOpen(true)
  }

  const handleEdit = (m: MeasurementItem) => {
    setEditing(m)
    setIsFormOpen(true)
  }

  const onCreate = async (data: CreateMeasurementData) => {
    await createM.mutateAsync(data)
  }

  const onUpdate = async (data: UpdateMeasurementData) => {
    await updateM.mutateAsync(data)
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteM.mutateAsync(id)
      toast.success('Measurement deleted')
      setDeletingId(null)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete')
    }
  }

  const { title, description } = getPageHeader(
    '/measurements',
    'Measurements',
    'Track vitals and body weight over time. Stored in canonical units (kg, mmHg).',
  )

  return (
    <PageContainer>
      <PageHeader
          leading={<PageHeaderLeading />}
          title={title}
          description={description}
          icon={<Thermometer className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              secondary={
                <>
                  <PageHeaderActionSecondary onClick={() => handleAdd('body_weight')}>
                    <Plus className="h-4 w-4" aria-hidden />
                    Weight
                  </PageHeaderActionSecondary>
                  <PageHeaderActionSecondary onClick={() => handleAdd('blood_pressure')}>
                    <Plus className="h-4 w-4" aria-hidden />
                    BP
                  </PageHeaderActionSecondary>
                  <PageHeaderActionSecondary onClick={() => handleAdd('heart_rate')}>
                    <Plus className="h-4 w-4" aria-hidden />
                    Heart rate
                  </PageHeaderActionSecondary>
                </>
              }
            />
          }
        />

        <MeasurementStats measurements={measurements} />

        <VisibleFilterFields
          listId="healthMeasurements"
          filters={filters}
          onFiltersChange={setFilters}
          definitions={measurementFilterDefinitions}
          ctx={undefined}
        />

        {error ? (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">Failed to load measurements: {error.message}</p>
              <div className="mt-4">
                <Button variant="outline" onClick={() => void refetch()}>
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {isLoading ? <HealthListPageSkeleton showFilters={false} ariaLabel="Loading measurements" /> : null}

        {!isLoading && !error ? (
          measurements.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center">
                <Thermometer className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <h3 className="mb-2 text-lg font-semibold">No measurements yet</h3>
                <p className="mb-4 text-muted-foreground">
                  Log weight, blood pressure, or heart rate to build your timeline.
                </p>
                <Button onClick={() => handleAdd('body_weight')}>
                  <Plus className="mr-2 h-4 w-4" />
                  Log first measurement
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {measurements.map((m) => (
                <Card
                  key={m.id}
                  className={
                    highlighted.has(m.id)
                      ? 'border-primary ring-1 ring-primary/30 transition-all duration-500'
                      : ''
                  }
                >
                  <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="secondary">{typeLabel(m.measurement_type)}</Badge>
                        <span className="text-sm text-muted-foreground">{formatMeasuredAt(m.measured_at)}</span>
                      </div>
                      <p className="font-medium">{measurementSummary(m)}</p>
                      {m.notes ? (
                        <p className="text-sm text-muted-foreground line-clamp-2">{m.notes}</p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button variant="outline" size="icon" type="button" onClick={() => handleEdit(m)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="icon" type="button" onClick={() => setDeletingId(m.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        ) : null}

        <MeasurementFormDialog
          open={isFormOpen}
          onOpenChange={(open) => {
            setIsFormOpen(open)
            if (!open) setEditing(null)
          }}
          measurement={editing}
          defaultType={createDefaultsType}
          onCreate={onCreate}
          onUpdate={onUpdate}
        />

        <AlertDialog open={deletingId !== null} onOpenChange={(open) => !open && setDeletingId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete measurement?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes the reading from your timeline. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => deletingId && void handleDelete(deletingId)}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </PageContainer>
  )
}
